// QuestionSorteer.js

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DOMPurify from 'dompurify'; // Import DOMPurify
import { checkAndInitializeDB, saveToDB, getFromDB } from "../../../component/indexedDB/indexedDB";// Adjust the import path as necessary
import QuestionPublicLink from "../../../component/PublicLink/PublicLink";
import {
  QuizDescription,
  QuizMessage,
  QuizMessageMedia, // Import the new styled component
  QuizScreen,
  QuizButton,
  QuizTitle,
  QuizDescriptionDiv,
} from "../../GeneralStyles";
import { CloseButton, QuizMessagePopUp } from "../../../component/modal/QuizMessagePopUp";
import QuizMarkdown from "../../../component/Markdown/QuizMarkdown";

// Styled Components for the Sortable List and Items
const QuizSorteerList = styled.div`
  max-width: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const QuizSorteerListItem = styled.div`
  width: 100%;
  padding: 15px 45px 15px 15px;
  margin-bottom: 15px;
  display: flex;
  flex-direction:column;
  align-items: center;
  position: relative;
  background: white;
  color: #001d29;
  border-radius: 5px;
  box-shadow: 0 4px 6px -1px #001d2910, 0 2px 4px -2px #001d2910;
  font-weight: 500;
  font-size: 18px;
  border: 1px solid #f0f0f0;
  cursor: grab;
  p {
    width: 100%;
    max-width: 800px;
    white-space: pre-line;
  }
  &.is-draggable {
    cursor: grabbing;
    z-index: 10;
  }
  &.is-disabled {
    background: #e0e0e0;
    cursor: not-allowed;
  }
  img {
    width: 100%;
  }
`;

const QuizSorteerListHandle = styled.div`
  position: absolute;
  right: 0;
  top:50%;
  transform: translateY(-50%);
  bottom:50%;
  width: 44px;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  &::after {
    content: "⠿";
    font-size: 25px;
    color: #00000099;
  }
`;

const QuestionSorteer = ({
  question,
  overViewPage,
  enableNavigationButton,
}) => {
  const [items, setItems] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [userOrder, setUserOrder] = useState([]);
  const [message, setMessage] = useState("");
  const [messageMedia, setMessageMedia] = useState(null); // New state for media
  const [attempts, setAttempts] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    const dbName = sessionStorage.getItem("currentSessionName") || "Leerling";
    checkAndInitializeDB(dbName);
    if (dbName !== "Leerling") {
      getFromDB(dbName).then((data) => {
        const attemptData = data.find(
          (item) => item.id === question.quiz_item_id
        );
        if (attemptData) {
          setAttempts(attemptData.attempts || 0);
          if (attemptData.answer) {
            setItems(attemptData.answer);
            setUserOrder(attemptData.answer);
            setOriginalOrder(question.sorteer_items);
            if (JSON.stringify(attemptData.answer) === JSON.stringify(question.sorteer_items)) {
              setIsDisabled(true);
            }
            enableNavigationButton(); // Enable navigation if there's already an answer
          } else {
            const randomizedItems = [...question.sorteer_items].sort(
              () => Math.random() - 0.5
            );
            setItems(randomizedItems);
            setUserOrder(randomizedItems);
            setOriginalOrder(question.sorteer_items);
          }
          if (
            question.maximum_attempts > 0 &&
            attemptData.attempts >= question.maximum_attempts
          ) {
            setIsDisabled(true);
          }
        } else {
          const randomizedItems = [...question.sorteer_items].sort(
            () => Math.random() - 0.5
          );
          setItems(randomizedItems);
          setUserOrder(randomizedItems);
          setOriginalOrder(question.sorteer_items);
        }
      });
    } else {
      const randomizedItems = [...question.sorteer_items].sort(
        () => Math.random() - 0.5
      );
      setItems(randomizedItems);
      setUserOrder(randomizedItems);
      setOriginalOrder(question.sorteer_items);
    }
  }, [question]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };


  const onDragEnd = (result) => {
    if (!result.destination || isDisabled) return;

    const updatedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(updatedItems);
    setUserOrder(updatedItems);
  };

  const handleSubmit = () => {
    const dbName = sessionStorage.getItem("currentSessionName") || "Leerling";
    const isCorrect = JSON.stringify(userOrder) === JSON.stringify(originalOrder);

    if (isCorrect) {
      setMessage(DOMPurify.sanitize(question.general_succes_text));
      setMessageMedia(question.general_succes_media); // Set success media
      setIsDisabled(true);
    } else {
      setMessage(DOMPurify.sanitize(question.general_error_text));
      setMessageMedia(question.general_error_media); // Set error media
    }

    const attempt = {
      id: question.quiz_item_id,
      answer: userOrder,
      success: isCorrect,
      attempts: attempts + 1,
    };
    saveToDB([attempt], dbName);
    setAttempts(attempts + 1);
    enableNavigationButton();

    if (
      question.maximum_attempts > 0 &&
      attempts + 1 >= question.maximum_attempts
    ) {
      setIsDisabled(true);
    }
  };

  const handleClosePopup = () => {
    setMessage(null);
    setMessageMedia([]);
  };

  // Helper function to render media based on mime type
  const renderMedia = (mediaItems) => {
    if (!mediaItems || mediaItems.length === 0) return null;

    return (
      <QuizMessageMedia>
        {mediaItems.map(media => {
          if (media.mime && media.mime.startsWith('image/')) {
            return <img style={{width:'50px'}} key={media.id} src={media.url} alt={media.name || 'media'} />;
          } else if (media.mime && media.mime.startsWith('audio/')) {
            return <audio key={media.id} autoPlay src={media.url} />;
          } else if (media.embed) {
            return <div key={media.id} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(media.embed) }} />;
          } else {
            return null;
          }
        })}
      </QuizMessageMedia>
    );
  };

  return (
    <QuizScreen
      bgColor={question.theme_color}
      bgVisible={overViewPage}
      id={question.quiz_item_id}
    >
      <QuestionPublicLink
        overViewPage={overViewPage}
        type="question"
        quiz_item_id={question.quiz_item_id}
      />
      <QuizTitle>{question.title}</QuizTitle>
      <QuizDescriptionDiv><QuizMarkdown>{question.description}</QuizMarkdown></QuizDescriptionDiv>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <QuizSorteerList
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {items.map((item, index) => (
                <Draggable
                  key={item.id.toString()}
                  draggableId={item.id.toString()}
                  index={index}
                  isDragDisabled={isDisabled}
                >
                  {(provided) => (
                    <QuizSorteerListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`list__item ${
                        isDisabled ? "is-disabled" : "is-idle"
                      } js-item`}
                    >
                      <QuizMarkdown>{item.content}</QuizMarkdown>
                      <QuizSorteerListHandle className="drag-handle js-drag-handle"></QuizSorteerListHandle>
                    </QuizSorteerListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </QuizSorteerList>
          )}
        </Droppable>
      </DragDropContext>
      {question.show_submit_button !== false && (
        <QuizButton
          onClick={handleSubmit}
          bgColor={question.theme_color}
          txtColor={question.theme_text_color}
          disabled={isDisabled}
        >
          {question.submit_button_Text}
        </QuizButton>
      )}
      {(message || (messageMedia && messageMedia.length > 0)) && (
        <QuizMessagePopUp>
           <CloseButton onClick={handleClosePopup} aria-label="Close Popup">&times;</CloseButton>
      {/* Render Media First */}
      {renderMedia(messageMedia)}
      {/* Updated QuizMessage to render sanitized HTML */}
      <QuizMessage className="message">
        <QuizMarkdown>{message}</QuizMarkdown>
        </QuizMessage>
      </QuizMessagePopUp>
      )}
    </QuizScreen>
  );
};

export default QuestionSorteer;
